import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { type ITabItem, toastStore } from 'shared/ui'
import { TableStore } from 'shared/ui/Table'
import { isMediaUrl } from 'shared/lib/isMediaUrl'
import {
  DayjsFormats,
  getAudioFileDuration,
  getBlob,
  isLink,
  logger,
  writeTextToClipboard,
} from 'shared/lib'
import { MAX_AUDIO_DURATION_SECONDS } from 'shared/constants/limits'
import { uiStore } from 'shared/store/uiStore'
import { ZIndex } from 'shared/constants/zIndex'
import { AttachmentsApi, type ILimitSizeError } from 'entities/Attachment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { getFileType } from 'entities/Attachment/lib/getFileType'
import { ContactsApi } from 'entities/Contacts'
import { type Contact } from 'entities/Contacts/model/Contact'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { downloadWithToast } from 'features/FileDownload'
import { MediaLibraryModalContent } from 'widgets/MediaLibrary/ui/MediaLibraryModal/MediaLibraryModalContent'
import {
  type IMediaLibraryStoreProps,
  type IMediaLibraryType,
  type IMediaLibraryViewType,
  type IOnAddAttachment,
  MediaLibrarySelectMode,
  MediaLibraryVariant,
} from 'widgets/MediaLibrary/types/types'
import { MEGABYTES_IN_GIGABYTE } from 'widgets/MediaLibrary/constants/constants'
import {
  getAttachmentFetchTypeByContentType,
  getAttachmentFlags,
  getMediaLibraryTypeByAttachmentType,
  isPicsnippetsUrl,
  sortAttachmentsByDate,
} from 'widgets/MediaLibrary/utils'
import { EnumVariantMessageField } from 'widgets/MessageField'
import { RenameModalStore } from 'widgets/RenameModal'
import { type PresentationModeStore } from 'widgets/PresentationMode'

export class MediaLibraryStore {
  private _fetchAttachmentList: ((term?: string) => Promise<Attachment[]>) | null = null
  private _onAddFile: ((file: File) => Promise<Attachment | null>) | null = null
  private _onDeleteAttachments: ((ids: Array<string | number>) => Promise<void>) | null = null
  private _modalId = ''
  private readonly _initContentType: IMediaLibraryType = 'all'
  private _contentType: IMediaLibraryType = 'all'
  private _viewType: IMediaLibraryViewType = 'grid'

  private _allAttachments: Map<string | number, Attachment> = new Map()
  private _activeTabAttachments: Attachment[] = []

  private _uploadInProgressIds: Set<string | number> = new Set()
  private _updatedTabs: Set<IMediaLibraryType> = new Set()
  private _uploadedCount = 0

  private _visibleColumnsIds: string[] = []
  private _isAttachmentLoading = false
  private _page = 1

  private readonly _onAddAttachment: IOnAddAttachment = () => {}
  private readonly _titleReaction: IReactionDisposer | null = null
  private readonly _perPage = 100

  private _selectMode: MediaLibrarySelectMode = MediaLibrarySelectMode.Multiple
  private _mediaLibraryVariant: MediaLibraryVariant = MediaLibraryVariant.MessageField

  private _isSearchResettingForUpload = false
  private _isAircallNumber = false
  private _isConversationMessageField = false
  private _conversationContact: Contact | null = null
  private _limitSizeErrors = new Map<string, number>()
  private _searchTerm = ''
  private _error = ''
  private _errorTitle = ''
  private _selectedAttachments: Map<string | number, Attachment> = new Map()
  private _spaceUsed: number | null = null
  private _totalAttachments: number | null = null
  private _tableStore = new TableStore<Attachment>({
    withoutDefaultManageColumns: true,
    element: 'attachment',
  })

  private _onRenameAudio: IMediaLibraryStoreProps['onRenameAudio'] | null = null

  private _uploading = false

  constructor(
    {
      onAddAttachment,
      messageFieldStore,
      mediaLibraryVariant,
      selectMode,
      fetchAttachmentList,
      onAddFile,
      onDeleteAttachments,
      onRenameAudio,
    }: IMediaLibraryStoreProps,
    private _entities: IEntitiesStore,
    private _presentationStore: PresentationModeStore
  ) {
    makeAutoObservable(this)

    if (fetchAttachmentList) {
      this._fetchAttachmentList = fetchAttachmentList
    }

    if (onAddFile) {
      this._onAddFile = onAddFile
    }

    if (onDeleteAttachments) {
      this._onDeleteAttachments = onDeleteAttachments
    }
    if (onRenameAudio) {
      this._onRenameAudio = onRenameAudio
    }
    this._onAddAttachment = onAddAttachment
    this._mediaLibraryVariant = mediaLibraryVariant

    if (selectMode) this._selectMode = selectMode
    if (this._mediaLibraryVariant === MediaLibraryVariant.MessageField && messageFieldStore) {
      this._isConversationMessageField =
        messageFieldStore.variant === EnumVariantMessageField.Conversation
      this._conversationContact = messageFieldStore.conversationContact || null
      this._isAircallNumber = messageFieldStore.isCurrentAirCall
    }

    if (
      this._mediaLibraryVariant === MediaLibraryVariant.RinglessSelect ||
      this._mediaLibraryVariant === MediaLibraryVariant.AudioSelect
    ) {
      this._initContentType = 'audio'
      this._contentType = 'audio'
    }

    this._titleReaction = reaction(
      () => this._spaceUsed,
      (currentSpaceUsed) => {
        const modal = modalStore.getModal(this._modalId)
        if (modal && currentSpaceUsed !== null && 'subtitle' in modal) {
          modal.subtitle =
            currentSpaceUsed === 0
              ? '0 / 3 GB used'
              : `${(currentSpaceUsed / MEGABYTES_IN_GIGABYTE).toFixed(2)} / 3 GB used`
        }
      }
    )
  }

  onMediaLibraryModalClose = () => {
    this._searchTerm = ''
    this._viewType = 'grid'
    this._page = 1
    this._contentType = this._initContentType
    this._selectedAttachments.clear()
    this._tableStore.resetSelected()
    modalStore.removeModal(this._modalId)
    this._titleReaction?.()
  }

  handleOpenMediaLibraryModal = () => {
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: 'Media',
      subtitle: '',
      width: 712,
      showCloseButton: false,
      ModalContent: MediaLibraryModalContent,
      ModalContentProps: {
        mediaLibraryStore: this,
      },
      onClose: this.onMediaLibraryModalClose,
    })

    if (this._entities.organizationStore.isActivityLoggingEnabled) {
      this._entities.organizationStore.handleTrackOrganizationActivity({
        event: 'phi.media.library.viewed',
      })
    }
    this.logMediaLibraryEvent('Open')
  }

  onSingleModeSelect = (id: string | number) => {
    const attachment = this._allAttachments.get(id)
    if (attachment) {
      this._onAddAttachment(attachment)
      this.logMediaLibraryEvent('File Selected')
      this.onMediaLibraryModalClose()
    }
  }

  onAddToConversation = (attachments: Attachment[], fromBulk = false) => {
    attachments.forEach((attachment) => {
      this._onAddAttachment(attachment)
    })
    if (fromBulk) {
      this._selectedAttachments.clear()
      this._tableStore.resetSelected()
    } else {
      attachments.forEach(({ id }) => {
        this._selectedAttachments.delete(id)
        const attachment = this.allAttachments.get(id)
        if (attachment) this._tableStore.toggleSelected(attachment, false)
      })
    }
    this.onMediaLibraryModalClose()
  }

  onRenameAttachment = (attachment: Attachment) => {
    if (this._onRenameAudio) {
      this._onRenameAudio(attachment)
      return
    }
    const renameModalId = nanoid()

    const handleCloseRenameModal = () => {
      modalStore.removeModal(renameModalId)
    }

    new RenameModalStore({
      id: renameModalId,
      title: 'Rename',
      name: attachment.name || '',
      withPostfix: true,
      element: 'File',
      isEditExtensionDisabled: true,
      secondaryAction: {
        text: 'Cancel',
        onAction: handleCloseRenameModal,
      },
      onClose: () => {
        handleCloseRenameModal()
      },

      onSave: async (name: string) => {
        try {
          const { data } = await AttachmentsApi.rename(attachment.id, name)
          const attachmentItem = this._allAttachments.get(attachment.id)
          if (attachmentItem && data) {
            attachmentItem.name = data.name
            this._allAttachments.set(attachment.id, attachmentItem)
          }
          this.setActiveTabAttachments()
          toastStore.add({
            type: 'success',
            title: 'File renamed',
          })
          this.logMediaLibraryEvent('File Renamed')
        } catch (e) {
          logger.error(e)
        } finally {
          handleCloseRenameModal()
        }
      },
    })
  }

  isVCardContactAbsent = async (phone?: string) => {
    if (phone) {
      const { data: isValid } = await ContactsApi.validateNumber({
        number: phone,
      })
      const { data } = await ContactsApi.getContactsList({
        search: phone,
      })

      return isValid && data.total === 0
    }
  }

  checkVCardIsSave = async (phone: string) => {
    this._entities.vCardStore.setIsLoading(true)
    const isAbsent = await this.isVCardContactAbsent(phone)
    this._entities.vCardStore.setIsLoading(false)

    if (this._entities.vCardStore.modalVCard) {
      this._entities.vCardStore.modalVCard.isSave = isAbsent
    }
  }

  onCopyAttachment = (attachment: Attachment) => {
    writeTextToClipboard(attachment?.source_short || '').then(() => {
      toastStore.add({
        type: 'info',
        title: 'Copied',
      })
      this.logMediaLibraryEvent('Copy URL')
    })
  }

  onOpenPresentationModal = (attachment: Attachment, attachments?: Attachment[]) => {
    const { isAudio, isVideo, isImage, isVCardPresentationAvailable } =
      getAttachmentFlags(attachment)

    if (attachments?.length) {
      const index = attachments.findIndex((elem) => elem.id === attachment.id)
      this._presentationStore.setAttachments(attachments, index, true)
    } else {
      if (isVCardPresentationAvailable && attachment.vCard) {
        this._entities.vCardStore.openVCard(attachment.vCard)
        this.checkVCardIsSave(attachment.vCard.phone)
        return
      }

      if (isAudio || isVideo || isImage) {
        this._presentationStore.setAttachments([attachment])
      }
    }
  }

  deleteAttachment = (id: number | string) => {
    this._allAttachments.delete(id)
  }

  onDeleteAttachments = async (ids: Array<string | number>, fromBulk = false) => {
    const deleteModalId = nanoid()

    const handleCloseDeleteModal = () => {
      modalStore.removeModal(deleteModalId)
    }

    const onDeleteAttachments = async () => {
      try {
        await (this._onDeleteAttachments
          ? this._onDeleteAttachments(ids)
          : AttachmentsApi.delete(ids))
        toastStore.add({
          type: 'success',
          title: ids.length > 1 ? ids.length + ' files deleted' : 'File deleted',
        })
        this._page = 1
        void this.fetchAttachmentList()
        this.logMediaLibraryEvent('File Deleted')
        if (fromBulk) {
          this._selectedAttachments.clear()
          this._tableStore.resetSelected()
        } else {
          ids.forEach((id) => {
            this._selectedAttachments.delete(id)
            const attachment = this.allAttachments.get(id)
            if (attachment) this._tableStore.toggleSelected(attachment, false)
          })
        }
      } catch (e) {
        logger.error(e)
      } finally {
        handleCloseDeleteModal()
      }
    }

    modalStore.addModal({
      id: deleteModalId,
      showHeader: true,
      showCloseButton: false,
      showCloseIcon: true,
      width: 280,
      type: ModalTypeList.ALERT,
      zIndex: ZIndex.OVERLAY_MIDDLE,
      title: ids.length > 1 ? `Delete ${ids.length} files?` : 'Delete file?',
      desc: 'This action cannot be undone',
      primaryAction: {
        text: 'Delete',
        onAction: onDeleteAttachments,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: handleCloseDeleteModal,
      },
      onClose: handleCloseDeleteModal,
    })
  }

  onTrackDownloadMedia = (ids: number[]) => {
    if (this._entities.organizationStore.isActivityLoggingEnabled) {
      this._entities.organizationStore.handleTrackOrganizationActivity({
        event: 'phi.file.downloaded',
        data: {
          file_ids: ids,
        },
      })
    }
  }

  onDownloadAttachment = async (attachment: Attachment) => {
    await downloadWithToast(
      async () => {
        const name = attachment.name || attachment.id.toString()
        const url = attachment.source

        if (!url) throw new Error('The attachment does not have a download URL')

        const blob = await getBlob(url)

        if (!blob) {
          throw new Error('Failed to download Blob from ' + url)
        }

        return { blob, name }
      },
      () => this.onDownloadAttachment(attachment)
    )
    this.onTrackDownloadMedia([Number(attachment.id)])
  }

  onBulkDownloadMedia = async (ids: Array<string | number>) => {
    await downloadWithToast(
      async () => {
        const { data } = await AttachmentsApi.getDownloadLink(ids)

        const name =
          ids.length === 1
            ? this._allAttachments.get(ids[0])?.name || 'attachment'
            : 'Salesmsg_' + uiStore.dayjs(new Date()).format(DayjsFormats.downloadFile)
        const blob = await getBlob(data.link)

        if (!blob) {
          throw new Error('Failed to download Blob from ' + data.link)
        }

        return { blob, name }
      },
      () => this.onBulkDownloadMedia(ids)
    )
    this.onTrackDownloadMedia(ids.map((id) => Number(id)))
  }

  handleSelectContentType = (tab: ITabItem<IMediaLibraryType>) => {
    this._contentType = tab.key
    this._page = 1
    this.setActiveTabAttachments()
  }

  getAttachmentsByType = (type: IMediaLibraryType): Attachment[] => {
    return Array.from(this._allAttachments.values()).filter((attachment) => {
      switch (type) {
        case 'images':
          return (
            attachment.type === 'image' ||
            attachment.type === 'gif' ||
            attachment.type === 'previewGif'
          )
        case 'videos':
          return attachment.type === 'video'
        case 'audio':
          return attachment.type === 'audio' || attachment.type === 'voice'
        case 'files':
          return attachment.type === 'attachment' || attachment.type === 'vcard'
        case 'all':
        default:
          return true
      }
    })
  }

  setActiveTabAttachments = () => {
    this._activeTabAttachments = this.getAttachmentsByType(this._contentType).sort(
      sortAttachmentsByDate
    )
  }

  setVisibleColumnsIds = (ids: string[]) => {
    this._visibleColumnsIds = ids
  }

  toggleViewType = () => {
    if (this._viewType === 'grid') {
      this._viewType = 'list'
      this.logMediaLibraryEvent('Switch to List view')
    } else {
      this._viewType = 'grid'
      this.logMediaLibraryEvent('Switch to Grid view')
    }
  }

  toggleAttachmentSelection = (id: string | number, skipLog = false) => {
    if (this._selectedAttachments.has(id)) {
      this._selectedAttachments.delete(id)
    } else {
      const attachment = this._allAttachments.get(id)
      if (attachment) {
        this._selectedAttachments.set(id, attachment)
        if (!skipLog) {
          this.logMediaLibraryEvent('File Selected')
        }
      }
    }
  }

  toggleAllSelected = () => {
    if (this.isAllSelected) {
      this._selectedAttachments.clear()
      this._tableStore.resetSelected()
    } else {
      this._tableStore.selectAllOnPageIds()
      this._activeTabAttachments.forEach((attachment: Attachment) => {
        this._selectedAttachments.set(attachment.id, attachment)
      })
      this.logMediaLibraryEvent('File Selected')
    }
  }

  setSelectedIdsFromTable = (ids: Array<string | number>) => {
    const newIdsSet = new Set(ids)
    const idsToSelect = ids.filter((id) => !this._selectedAttachments.has(id))
    const idsToUnselect = [...this._selectedAttachments.keys()].filter((id) => !newIdsSet.has(id))

    const allChanges = [...idsToSelect, ...idsToUnselect]

    allChanges.forEach((id) => this.toggleAttachmentSelection(id, true))
    if (idsToSelect.length > 0) {
      this.logMediaLibraryEvent('File Selected')
    }
  }

  setSearchTerm = (searchTerm: string) => {
    this._searchTerm = searchTerm
  }

  isSelected = (id: string | number) => {
    return this._selectedAttachments.has(id)
  }

  loadNextPage = () => {
    this._page = this._page + 1
  }

  fetchAttachmentList = async (searchTerm?: string) => {
    try {
      runInAction(() => {
        this._isAttachmentLoading = true
      })

      if (this._fetchAttachmentList) {
        const attachments = await this._fetchAttachmentList(searchTerm)
        runInAction(() => {
          this._allAttachments = new Map(attachments.map((item) => [item.id, item]))
        })
        this.setActiveTabAttachments()
      } else {
        const attachmentFetchType = getAttachmentFetchTypeByContentType(this._contentType)
        const { data } = await AttachmentsApi.list(
          this._page,
          this._perPage,
          attachmentFetchType,
          searchTerm
        )

        if (searchTerm) {
          this.logMediaLibraryEvent('Search')
        }

        runInAction(() => {
          const allAttachmentsArray: [string | number, Attachment][] = [
            ...data.attachments.image.map((attachment): [string | number, Attachment] => [
              attachment.id,
              new Attachment({ responseMedia: attachment }),
            ]),
            ...data.attachments.video.map((attachment): [string | number, Attachment] => [
              attachment.id,
              new Attachment({ responseMedia: attachment }),
            ]),
            ...data.attachments.audio.map((attachment): [string | number, Attachment] => [
              attachment.id,
              new Attachment({ responseMedia: attachment }),
            ]),
            ...data.attachments.docs.map((attachment): [string | number, Attachment] => {
              return [attachment.id, new Attachment({ responseMedia: attachment })]
            }),
          ]

          if (this._page === 1) {
            this._allAttachments = new Map(allAttachmentsArray)
          } else {
            this._allAttachments = new Map([...this._allAttachments, ...allAttachmentsArray])
          }

          this._spaceUsed = data.space_used
          this._totalAttachments = data.total
          this.setActiveTabAttachments()
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isAttachmentLoading = false
      })
    }
  }

  setLimitSizeError = ({ label, limit }: ILimitSizeError) => {
    if (this._limitSizeErrors.has(label)) return

    this._limitSizeErrors.set(label, limit)
    setTimeout(() => {
      runInAction(() => {
        this._limitSizeErrors.delete(label)
      })
    }, 5000)
  }

  deleteLimitSizeError = (id: string) => {
    this._limitSizeErrors.delete(id)
  }

  setError = (extError: string, titleExtError?: string) => {
    this._error = extError
    this._errorTitle = titleExtError || ''
    setTimeout(() => {
      runInAction(() => {
        this._error = ''
        this._errorTitle = ''
      })
    }, 5000)
  }

  addAttachmentViaUrl = async (url: string) => {
    const isUrl = isLink(url)

    const enableText = 'Unable to upload the file'
    const enterValidText = 'Please enter a valid file URL'

    if (!isUrl) {
      this.setError(enterValidText, enableText)
      return
    }

    const extension = url.split('.').pop()

    if (!isPicsnippetsUrl(url) && extension && extension.length > 4) {
      this.setError(enterValidText, enableText)

      return
    }

    const isValidUrl = isMediaUrl(url) || isPicsnippetsUrl(url)

    if (!isValidUrl) {
      this.setError('Please upload the correct file format (JPG, PNG, GIF, VCF)', enableText)
      return
    }

    try {
      runInAction(() => {
        this._uploading = true
      })

      const { data } = await AttachmentsApi.uploadLinkViaUrl(url)

      if (data.attachment) {
        const attachment = new Attachment({
          responseMedia: {
            ...data.attachment,
            name: data.attachment.name || 'attachment',
          },
        })

        this._onAddAttachment(attachment)
        this.onMediaLibraryModalClose()
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._uploading = false
      })
    }
  }

  addAttachment = (attachment: Attachment, updated = false) => {
    this._allAttachments.set(attachment.id, attachment)
    this.setActiveTabAttachments()

    if (updated) {
      if (this._uploadInProgressIds.has(attachment.id)) {
        this._uploadInProgressIds.delete(attachment.id)
        this._updatedTabs.add(getMediaLibraryTypeByAttachmentType(attachment.type))
      }
    } else {
      this._uploadInProgressIds.add(attachment.id)
      this._uploadedCount++
      return
    }

    this._uploadedCountToast()
  }

  private _uploadedCountToast = () => {
    if (this._uploadInProgressIds.size !== 0 || this._uploadedCount === 0) return

    const count = this._uploadedCount
    const crossTab =
      this._contentType !== 'all' &&
      (!this._updatedTabs.has(this._contentType) || this._updatedTabs.size > 1)
    const title = `${count} file${count > 1 ? 's' : ''} uploaded${
      crossTab ? ' to different tabs' : ''
    }`

    this._uploadedCount = 0
    this._updatedTabs.clear()
    toastStore.add({ type: 'success', title })
    this.logMediaLibraryEvent('File Uploaded')
  }

  handleUploadError = (attachmentId: number) => {
    if (this._uploadInProgressIds.has(attachmentId)) {
      this._uploadInProgressIds.delete(attachmentId)
      this._uploadedCount = Math.max(0, this._uploadedCount - 1)
    }
    this._allAttachments.delete(attachmentId)
    this.setActiveTabAttachments()
    this._uploadedCountToast()
    toastStore.add({ type: 'error', title: 'File upload failed. Please try again.' })
  }

  addFilesToLibrary = async (propsFiles: File[]) => {
    let files = [...propsFiles]
    if (this._searchTerm) {
      this._isSearchResettingForUpload = true

      await this.fetchAttachmentList()

      this._isSearchResettingForUpload = false
    }
    if (this._isAircallNumber && !this._entities.featureFlagsStore.aircall_new_api) {
      const { videoAccept, fileAccept } = this._entities.attachmentStore
      const isValidAircallUpload = files.every((file) => {
        const fileType = file.type
        const fileExtension = file.name.split('.').pop()?.toLowerCase()

        return (
          fileAccept.includes(fileType) ||
          videoAccept.includes(fileType) ||
          videoAccept.includes(`.${fileExtension}`)
        )
      })

      if (!isValidAircallUpload) {
        toastStore.add({
          type: 'error',
          title: 'One or more files are not allowed to be uploaded.',
        })
        return
      }
    }

    if (this.isRinglessSelectVariant || this.isAudioSelectVariant) {
      const { audioAccept, checkFilesSizeLimitsForUpload } = this._entities.attachmentStore
      files = await checkFilesSizeLimitsForUpload({
        files,
        setLimitSizeError: this.setLimitSizeError,
      })
      const validationResults = await Promise.all(
        files.map(async (file) => {
          if (file.type && file.type !== 'application/octet-stream') {
            return audioAccept.includes(file.type)
          }
          const detectedType = await getFileType(file)
          return detectedType === 'audio'
        })
      )

      if (!validationResults.every(Boolean)) {
        toastStore.add({
          type: 'error',
          title: 'Only audio files are allowed to be uploaded.',
        })
        return
      }

      if (this.isAudioSelectVariant) {
        try {
          const durations = await Promise.all(files.map(getAudioFileDuration))
          const hasInvalidDuration = durations.some(
            (duration) => Math.round(duration) > MAX_AUDIO_DURATION_SECONDS
          )
          if (hasInvalidDuration) {
            toastStore.add({
              type: 'error',
              title: 'Audio must be less than 3 minutes.',
            })
            return
          }
        } catch {
          toastStore.add({
            type: 'error',
            title: 'Only audio files are allowed to be uploaded.',
          })
          return
        }
      }
    }
    if (this._onAddFile) {
      const onAddFile = this._onAddFile
      const tempAttachments = files.map((file) => new Attachment({ file, loading: true }))
      runInAction(() => {
        tempAttachments.forEach((temp) => {
          this._allAttachments.set(temp.id, temp)
          this._uploadInProgressIds.add(temp.id)
          this._uploadedCount++
        })
        this.setActiveTabAttachments()
      })

      await Promise.all(
        tempAttachments.map(async (tempAttachment, index) => {
          try {
            const attachment = await onAddFile(files[index])
            runInAction(() => {
              this._allAttachments.delete(tempAttachment.id)
              this._uploadInProgressIds.delete(tempAttachment.id)
              if (attachment) {
                this._allAttachments.set(attachment.id, attachment)
                this._updatedTabs.add(getMediaLibraryTypeByAttachmentType(attachment.type))
              } else {
                this._uploadedCount = Math.max(0, this._uploadedCount - 1)
              }
              this.setActiveTabAttachments()
              this._uploadedCountToast()
            })
          } catch (e) {
            logger.error(e)
            runInAction(() => {
              this._allAttachments.delete(tempAttachment.id)
              this._uploadInProgressIds.delete(tempAttachment.id)
              this._uploadedCount = Math.max(0, this._uploadedCount - 1)
              this.setActiveTabAttachments()
              this._uploadedCountToast()
            })
            toastStore.add({ type: 'error', title: 'File upload failed. Please try again.' })
          }
        })
      )
      return
    }

    await this._entities.attachmentStore.addMediaFiles({
      files,
      addAttachment: this.addAttachment,
      setExtError: this.setError,
      setLimitSizeError: this.setLimitSizeError,
    })
  }

  logMediaLibraryEvent = (eventName: string) => {
    this._entities.eventLogStore.logEvent(
      'Media Library Used',
      {
        event_id: 'media_library_used',
        action: eventName,
      },
      { groupId: this._entities.organizationStore.id }
    )
  }

  setSpaceUsed = (value: number) => {
    this._spaceUsed = value
  }

  get selectedViewType() {
    return this._viewType
  }

  get visibleColumnsIds() {
    return this._visibleColumnsIds
  }

  get selectedContentType() {
    return this._contentType
  }

  get isActiveTabEmpty() {
    return this._activeTabAttachments.length === 0
  }

  get activeTabAttachments() {
    if (this._isAircallNumber && !this._entities.featureFlagsStore.aircall_new_api) {
      return this._activeTabAttachments.filter((attachment) =>
        ['video', 'attachment'].includes(attachment.type)
      )
    }
    return this._activeTabAttachments
  }

  get isAllSelected() {
    return this._activeTabAttachments.every((attachment) =>
      this._selectedAttachments.has(attachment.id)
    )
  }

  get isAnyChecked() {
    return this._selectedAttachments.size > 0
  }

  get isIndeterminate() {
    return this._selectedAttachments.size > 0 && !this.isAllSelected
  }

  get allAttachments() {
    return this._allAttachments
  }

  get allAttachmentsIds() {
    return Array.from(this._allAttachments.keys())
  }

  get isAnyFileWaitingForUpload() {
    return this._uploadInProgressIds.size > 0
  }

  get currentPage() {
    return this._page
  }

  get isAttachmentInitListLoading() {
    return this.currentPage === 1 && this._isAttachmentLoading
  }

  get isLoadingMore() {
    return this.currentPage > 1 && this._isAttachmentLoading
  }

  get hasMore() {
    return this._totalAttachments && this._totalAttachments > this._page * this._perPage
  }

  get isRinglessSelectVariant() {
    return this._mediaLibraryVariant === MediaLibraryVariant.RinglessSelect
  }

  get isAudioSelectVariant() {
    return this._mediaLibraryVariant === MediaLibraryVariant.AudioSelect
  }

  get isDefaultVariant() {
    return this._mediaLibraryVariant === MediaLibraryVariant.Default
  }

  get isSingleSelectMode() {
    return this._selectMode === MediaLibrarySelectMode.Single
  }

  get uploading() {
    return this._uploading
  }

  get searchTerm() {
    return this._searchTerm
  }

  get isSearchResettingForUpload() {
    return this._isSearchResettingForUpload
  }

  get isConversationMessageField() {
    return this._isConversationMessageField
  }

  get conversationContact() {
    return this._conversationContact
  }

  get isAircallNumber() {
    return this._isAircallNumber
  }

  get limitSizeErrors() {
    return this._limitSizeErrors
  }

  get error() {
    return this._error
  }

  get errorTitle() {
    return this._errorTitle
  }

  get mediaLibraryVariant() {
    return this._mediaLibraryVariant
  }

  get selectedAttachments() {
    return this._selectedAttachments
  }

  get tableStore() {
    return this._tableStore
  }
}
